import * as THREE from 'three';

const MAX_LAMPS = 8;

export function createGridGlowOverlay(size, divisions, y = 0.052) {
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uGridScale: { value: Math.max(2, divisions / Math.max(1, size)) },
      uBaseOpacity: { value: 0.03 },
      uGlowOpacity: { value: 0.2 },
      uPlayerPos: { value: new THREE.Vector2(0, 0) },
      uPlayerRadius: { value: 6.6 },
      uLampCount: { value: 0 },
      uLampPos: { value: Array.from({ length: MAX_LAMPS }, () => new THREE.Vector2(9999, 9999)) },
      uLampRadius: { value: 7.2 },
    },
    vertexShader: `
      varying vec2 vWorldXZ;
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorldXZ = world.xz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: `
      uniform float uGridScale;
      uniform float uBaseOpacity;
      uniform float uGlowOpacity;
      uniform vec2 uPlayerPos;
      uniform float uPlayerRadius;
      uniform int uLampCount;
      uniform vec2 uLampPos[${MAX_LAMPS}];
      uniform float uLampRadius;
      varying vec2 vWorldXZ;

      float lineMask(vec2 world) {
        vec2 gv = abs(fract(world * uGridScale) - 0.5);
        float nearest = min(gv.x, gv.y);
        return 1.0 - smoothstep(0.02, 0.055, nearest);
      }

      float radialGlow(vec2 p, vec2 c, float radius) {
        float d = distance(p, c);
        return 1.0 - smoothstep(radius * 0.35, radius, d);
      }

      void main() {
        float mask = lineMask(vWorldXZ);
        float playerGlow = radialGlow(vWorldXZ, uPlayerPos, uPlayerRadius);
        float lampGlow = 0.0;
        for (int i = 0; i < ${MAX_LAMPS}; i++) {
          if (i >= uLampCount) break;
          lampGlow = max(lampGlow, radialGlow(vWorldXZ, uLampPos[i], uLampRadius));
        }
        float glow = max(playerGlow, lampGlow * 0.92);
        float alpha = mask * (uBaseOpacity + glow * uGlowOpacity);
        vec3 baseColor = mix(vec3(0.38, 0.12, 0.24), vec3(1.0, 0.22, 0.48), glow);
        gl_FragColor = vec4(baseColor, alpha);
      }
    `,
  });

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = y;
  mesh.renderOrder = 6;
  return mesh;
}

export function updateGridGlowOverlay(mesh, { playerX, playerZ, playerRadius, lampPoints = [], lampRadius = 7.2 }) {
  if (!mesh?.material?.uniforms) return;
  const u = mesh.material.uniforms;
  u.uPlayerPos.value.set(playerX, playerZ);
  u.uPlayerRadius.value = playerRadius;
  const count = Math.min(MAX_LAMPS, lampPoints.length);
  u.uLampCount.value = count;
  u.uLampRadius.value = lampRadius;
  for (let i = 0; i < MAX_LAMPS; i++) {
    const p = i < count ? lampPoints[i] : null;
    u.uLampPos.value[i].set(p?.x ?? 9999, p?.z ?? 9999);
  }
}

